// Default English translations for the fan-facing web app
// These serve as fallbacks when no translations exist in the database.
//
// Since the translation ledger, core_translations.en — seeded from this object by
// scripts/gen-core-en-seed.mjs — shadows these values at runtime AND is the source
// text translate-club's 'platform' scope sends to DeepL for every other locale.
// Editing an English label here therefore changes nothing on its own: regenerate
// the seed into a NEW migration (the generator's header has the command) and apply
// it, or edit the row in Admin → Translations.

export const DEFAULT_TRANSLATIONS: Record<string, string> = {
  // Common
  'common.backToHome': 'Back To Home',
  'common.getStarted': 'GET STARTED',
  'common.email': 'Email',
  'common.submit': 'Submit',
  'common.loading': 'Loading...',
  'common.noActiveMatch': 'No active match',
  'common.section': 'Section',
  'common.row': 'Row',
  'common.seat': 'Seat',
  'common.screenTag': 'Joined via',
  'common.poweredBy': 'Powered by IDSeat',
  'common.privacyPolicy': 'Privacy Policy',
  'common.termsConditions': 'Terms & Conditions',
  'common.settings': 'Settings',
  'common.language': 'Language',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.openingSoon': 'Opening soon',
  'common.locked': 'Locked',
  'common.officialPartners': 'Official Partners',
  'error.scanLimitExceeded': 'Security limit reached for this seat today',
  'error.securityLimitTitle': 'Security Notice',
  'error.securityLimitMessage': 'This seat\'s digital access has reached its limit for today\'s match. For security, interactive features are locked.',
  'error.reScanRequiredTitle': 'Re-scan Required',
  'error.reScanRequiredMessage': 'For security, please re-scan the QR code at your seat to access the match experience.',
  'error.securityRef': 'Ref: LCK-409',
  'error.scanTagTitle': 'Scan the QR code',
  'error.scanTagMessage': 'To join from here, scan the QR code on the stadium screen or on the printed sign. A shared link will not work.',

  // Language Settings
  'languageSettings.selectPreferred': 'Select your preferred language',

  // Predictor
  'predictor.title': 'Score Predictor',
  'predictor.description': 'Predict the score of today\'s match and win exclusive rewards!',
  'predictor.introWithPrizes': 'Predict → chance to spin & win',
  'predictor.introNoPrizes': 'Predict the final score of today\'s match!',
  'predictor.submitButton': 'Submit Prediction',
  'predictor.submitted': 'Prediction submitted!',
  'predictor.alreadySubmittedTitle': 'Already Submitted',
  'predictor.alreadySubmittedMessage': 'You\'ve already submitted your prediction for this match. Good luck!',
  'predictor.closedTitle': 'Predictions Closed',
  'predictor.closedMessage': 'The match has already started. Predictions are no longer being accepted.',
  'predictor.openingSoon': 'Predictor becomes available closer to kickoff',
  'predictor.spinToWin': 'Spin to Win!',
  'predictor.chanceToWin': '🎉 A chance to win ',
  'predictor.prizeWinMessage': 'If your prediction is correct, you will be entered into a prize draw to win your prize. If successful, you will be contacted by email with more details.',
  'predictor.yourPrediction': 'Your prediction',
  'predictor.finalScore': 'Final score',
  'predictor.prizeKept': 'Prize wheel: {{prize}}. Show the confirmation email at the kiosk.',
  'predictor.resultCorrectTitle': 'You got it right!',
  'predictor.resultWrongTitle': 'Not this time',
  'predictor.drawNote': 'The club draws one winner among the correct predictions and contacts them by email.',
  'predictor.stripPending': 'Your prediction {{h}}–{{a}}',
  'predictor.stripLive': 'Your prediction {{h}}–{{a}} · live {{lh}}–{{la}}',
  'predictor.stripCorrect': 'Correct! {{lh}}–{{la}} · you are in the draw',
  'predictor.stripCorrectPlain': 'Correct! {{lh}}–{{la}}',
  'predictor.stripWrong': 'Final score {{lh}}–{{la}} · you predicted {{h}}–{{a}}',
  'predictor.resultCorrectPlain': 'You predicted the final score.',
  'predictor.resultWrongNote': 'Thanks for playing! The final score is above.',

  // Survey
  'survey.title': 'Quick Survey',
  'survey.introWithPrizes': 'Complete the survey for a chance to spin & win',
  'survey.introNoPrizes': 'Share your feedback with us!',
  'survey.submitButton': 'Submit Survey',
  'survey.thankYou': 'Thank you for your feedback!',
  'survey.selectSeveral': 'You can choose more than one',
  'survey.alreadyCompletedTitle': 'Already Completed',
  'survey.alreadyCompletedMessage': 'You\'ve already completed this survey. Thank you for your feedback!',
  'survey.closedTitle': 'Survey Closed',
  'survey.closedMessage': 'The survey for this match has now closed. Thank you for your interest!',
  'survey.openingSoon': 'Survey becomes available closer to kickoff',
  'survey.closed': 'Survey is no longer open for this fixture',
  'survey.prizeWinMessage': 'Thanks for completing the survey, you will now be entered into a prize draw to win your prize. If successful, you will be contacted by email with more details.',
  'survey.yourAnswer': 'Your answer...',

  // Lineups
  'lineups.title': 'Line-ups',
  'lineups.description': 'Starting XI for today\'s match',
  'lineups.notAvailable': 'Not available yet',
  'lineups.openingSoon': 'Lineups become available closer to kickoff',
  'lineups.home': 'Home',
  'lineups.away': 'Away',

  // Match Analytics — DB-backed in core_translations for non-English locales.
  // These English entries duplicate the seed so the fallback never relies on
  // the inline defaultValue passed to t(), AND so the admin Translations
  // page surfaces every key as editable (it iterates DEFAULT_TRANSLATIONS).
  'match.title': 'Match Analytics',
  'match.description': 'Live timeline, stats, lineups & standings',
  'match.tab.timeline': 'Timeline',
  'match.tab.stats': 'Stats',
  'match.tab.lineup': 'Lineup',
  'match.tab.standings': 'Standings',
  'match.timeline.waitingTitle': 'Awaiting first event',
  'match.timeline.waitingHint': 'Goals, cards and substitutions will appear here in real time.',
  'match.stats.empty': 'Match statistics will appear once the match starts.',
  'match.lineup.empty': 'Lineup will appear closer to kickoff.',
  'match.standings.empty': 'Standings not available yet.',
  'match.standings.team': 'Team',
  'match.standings.played': 'P',
  'match.standings.gd': 'GD',
  'match.standings.points': 'Pts',
  'match.standings.form': 'Form',

  // Consent
  'consent.termsPrefix': 'By ticking this box, you agree to',
  'consent.termsSuffix': 'and allow us to use your email in line with these policies.',
  'consent.marketing': 'I agree to receive marketing communications from {{clubName}} and its official sponsors and partners, including updates, offers, and promotions.',
  'consent.and': 'and',

  // Errors
  'error.tagNotFound': 'Tag not found',
  'error.scanAgain': 'This tag could not be found. Please scan again.',
  'error.invalidEmail': 'Please enter a valid email address',
  'error.requiredQuestions': 'Please answer all required questions',
  'error.acceptTerms': 'Please accept the Terms & Conditions and Privacy Policy',
  'error.genericSubmit': 'Failed to submit. Please try again.',

  // Prizes
  'prizes.more': 'more',

  // Live Score Status
  'liveScore.live': 'Live',
  'liveScore.halfTime': 'HT',
  'liveScore.fullTime': 'FT',

  // Ordering
  'ordering.title': 'Order to your Seat',
  'ordering.tapToAdd': 'Tap an item to add it to your order',
  'ordering.servedBy': 'Served by {{name}}',
  'ordering.add': 'Add',
  'ordering.customizable': 'Customizable',
  'ordering.viewCart': 'View cart',
  'ordering.titlePickup': 'Order from your Seat',
  'ordering.addToCart': 'Add',
  'ordering.cart': 'Cart',
  'ordering.confirmOrder': 'Confirm Order',
  'ordering.orderReceived': 'Order Received!',
  'ordering.orderReceivedMessage': 'Your order has been received and will be delivered to your seat shortly.',
  'ordering.orderReceivedPickupMessage': 'Your order has been received. You will be notified when it is ready for pick-up.',
  'ordering.orderDelivered': 'Order Delivered!',
  'ordering.orderDeliveredMessage': 'Your order has been delivered to your seat.',
  'ordering.orderCollected': 'Order Collected!',
  // Compact status pill shown on every screen outside the ordering view.
  'ordering.pill.processing': 'Payment processing…',
  'ordering.pill.received': 'Order received — being prepared',
  'ordering.pill.ready': 'Your order is ready',
  'ordering.pill.delivered': 'Order delivered',
  'ordering.pill.collected': 'Order collected',
  'ordering.pill.cancelled': 'Order cancelled',
  'ordering.pill.refunded': 'Order refunded',
  'payment.receivedReload': 'Payment received — reload the page to see your order.',
  'ordering.orderCollectedMessage': 'Your order has been picked up. Enjoy!',
  'ordering.emptyCart': 'Your cart is empty',
  'ordering.backToMenu': 'Back to Menu',
  'ordering.callStaff': 'Call Staff',
  'ordering.pickupMode': 'Pick-up Order',
  'ordering.deliveryMode': 'Delivery to your seat',
  'ordering.phoneNumber': 'Phone number (optional)',
  // "Both" sections — the fan chooses delivery vs pick-up in the cart.
  'ordering.titleBoth': 'Order Food & Drinks',
  'ordering.chooseFulfillment': 'How do you want to receive your order?',
  'ordering.deliveryOption': 'Seat delivery',
  'ordering.pickupOption': 'Pick-up',
  'ordering.orderReadyAtCollection': '📍 Your order is ready! Head to the collection point.',
  'ordering.orderReadyAtCollectionTitle': 'Ready for Pick-up! 📍',
  'ordering.orderReadyAtCollectionMessage': 'Your order is ready. Head to the collection point now.',

  'ordering.total': 'Total',
  'ordering.refreshStatus': 'Refresh Status',
  'ordering.items': 'items',
  'ordering.itemsNoLongerAvailable': 'Some items in your cart are no longer available and have been removed.',
  'ordering.emptyMenu': 'No items available at the moment',
  'ordering.openingSoon': 'Ordering becomes available closer to kickoff',
  'ordering.paused': 'Ordering is temporarily paused — back shortly',
  'ordering.pausedBadge': 'Paused',
  'ordering.chooseAddons': 'Personalize your item',
  'ordering.chooseOne': 'Choose one',
  'ordering.optionalExtras': 'Optional extras',
  'ordering.addonChoiceRequired': 'An item needs a choice (for example the kind of tea). Remove it from your order and add it again to choose.',
  'ordering.addedToCart': 'Added to cart',
  'ordering.specialInstructions': 'Any additional comments?',
  'ordering.addItem': 'Add to Order',
  'ordering.commentsPlaceholder': 'e.g. No onions, extra napkins...',

  // Order giveaway (the prize itself is club text, translated per club
  // under ordering.giveawayPrize — deliberately NOT listed here so the
  // untranslated sentinel works; see src/lib/giveaway.ts).
  'ordering.giveawayPitch': "If you add your email and complete your order, you'll be entered into a giveaway to win {{prize}}!",
  'ordering.giveawayEmailPlaceholder': 'your@email.com',
  'ordering.giveawayPrivacy': 'Optional. By entering you agree to the club\'s Privacy Policy and Terms and Conditions.',
  'ordering.orderAgain': 'Order Again',
  'ordering.giveawayEnteredTitle': "You're in!",
  'ordering.giveawayEnteredMessage': "You've also been entered into the giveaway, you'll be contacted by email if you're the winner.",

  // Per-item seat delivery — see isPickupOnly/pickupOnlyNames in orderingMode.ts.
  'ordering.pickupOnlyBadge': 'Pick-up only',
  'ordering.pickupOnlyNote': 'The following items can\'t be ordered to your seat: {{items}}. You can collect the entire order at {{point}} or remove {{items}} to have your order delivered to your seat.',
  'ordering.notAvailableInSection': 'Not available in this section',
  'ordering.errorDeliveryItemNotAllowed': 'Some items in your order are pick-up only — choose Pick-up or remove them.',
  'ordering.errorItemNotForTenant': 'Some items are not offered at this match and were removed from your order.',
  // "Pick-up now" club switch off: pick-up only as a half-time pre-order.
  'ordering.pickupHalfTimeOnlyClosed': 'Pick-up is available as a half-time pre-order only — pre-orders are closed for this match',
  'ordering.errorPickupNowDisabled': 'Pick-up is only available as a half-time pre-order.',
  'ordering.pickupHeldPreorder': 'Pick-up is available as a half-time pre-order only — you already have one for this match.',
  // Cart fulfilment summary card, pick-up now (see renderFulfilmentSummary in TagPage).
  'ordering.pickupNowAt': 'Pick-up now · {{point}}',
  'ordering.pickupNowReadyNote': "We'll tell you when it's ready.",

  // Dietary labels & allergens — fixed vocabulary, see src/lib/dietary.ts.
  'ordering.itemInfo': 'Item info',
  'ordering.contains': 'Contains',
  'ordering.ingredients': 'Ingredients',
  'ordering.dietary.vegetarian': 'Vegetarian',
  'ordering.dietary.vegan': 'Vegan',
  'ordering.dietary.gluten_free': 'Gluten-free',
  'ordering.allergen.gluten': 'Gluten',
  'ordering.allergen.crustaceans': 'Crustaceans',
  'ordering.allergen.eggs': 'Eggs',
  'ordering.allergen.fish': 'Fish',
  'ordering.allergen.peanuts': 'Peanuts',
  'ordering.allergen.soybeans': 'Soy',
  'ordering.allergen.milk': 'Milk',
  'ordering.allergen.nuts': 'Nuts',
  'ordering.allergen.celery': 'Celery',
  'ordering.allergen.mustard': 'Mustard',
  'ordering.allergen.sesame': 'Sesame',
  'ordering.allergen.sulphites': 'Sulphites',
  'ordering.allergen.lupin': 'Lupin',
  'ordering.allergen.molluscs': 'Molluscs',

  // Payment
  'payment.loadingSecure': 'Loading secure payment…',
  'payment.loadError': "Couldn't load the payment form — close and try again.",
  // Checkout and order outcome texts that only lived as code fallbacks until
  // 23 Oct 2026, so they were never seeded or translated (fanTranslationKeys guard).
  'payment.payNow': 'Pay Now',
  'payment.pay': 'Pay',
  'payment.payInPerson': 'Pay In Person',
  'payment.title': 'Secure checkout',
  'payment.enterDetails': 'Pay securely with card or wallet',
  'payment.securedByStripe': 'Secured by Stripe',
  'payment.subtotal': 'Subtotal',
  'payment.serviceFee': 'Service fee',
  'payment.total': 'Total',
  'payment.genericError': 'Payment failed. Please try again.',
  'payment.failed': 'Payment failed. Please try another method.',
  'payment.processing': 'Payment is processing. We will update your order shortly.',
  'payment.success': 'Payment successful! Your order is on its way.',
  'payment.orderCancelled': 'Your order has been cancelled.',
  'payment.orderCancelledTitle': 'Order Cancelled',
  'payment.orderCancelledMessage': 'Your order has been cancelled. No payment was charged.',
  'payment.orderRefunded': 'Your order has been refunded.',
  'payment.orderRefundedTitle': 'Order Refunded',
  'payment.orderRefundedMessage': 'Your order has been refunded. The amount will be returned to your payment method within a few business days.',
  'payment.refundAmount': 'Refund Amount',
  'ordering.menu': 'Menu',
  'ordering.subtotal': 'Subtotal',
  'ordering.serviceFee': 'Service fee',
  'ordering.removeOne': 'Remove one',
  'ordering.cancelAndChange': 'Cancel & change items',
  'ordering.processingPayment': 'Processing payment…',
  'ordering.processingPaymentMessage': "We're confirming your payment with the provider. This usually takes a few seconds.",
  'receipt.viewReceipt': 'View Receipt',
  'poll.title': 'Poll',
  // A club link opened inside the fan page (LinkSheet)
  'cta.openInBrowser': 'Open in browser',
  'cta.loading': 'Loading…',
  'cta.openSite': 'Open {{host}}',
  'cta.previewHint': 'This site opens in your browser — the match page stays open here.',
  'event.updated': 'Match data updated',

  // Half-time pre-orders (fan page)
  'preorder.chip': 'Skip the half-time queue — pre-order now, collect at {{point}} at half-time',
  'preorder.whenTitle': 'When do you want it?',
  'preorder.now': 'Now',
  'preorder.halfTime': 'Half-time pre-order',
  'preorder.collectAt': 'Collect at {{point}} at half-time — paid now, no queue later.',
  'preorder.closesAt': "Pre-orders close at {{minute}}'",
  // Cart fulfilment summary card, half-time pre-order.
  'preorder.paidNowTag': 'Paid now',
  'preorder.summaryReady': 'Ready for you at half-time, no queue. Pre-orders close at {{minute}}′.',
  'preorder.collectAtPoint': 'Collect at {{point}}',
  'preorder.closed': 'Half-time pre-orders are closed for this match',
  'preorder.payButton': 'Pre-order for half-time',
  'preorder.confirmedTitle': 'Pre-order confirmed for half-time ✓',
  'preorder.confirmedMessage': 'Head to {{point}} at half-time and collect your order. We will keep you updated on the progress of the order here.',
  'preorder.preparingTitle': 'Being prepared for half-time',
  'preorder.preparingMessage': "The kitchen is preparing your pre-order. You'll see it here as soon as it is ready at {{point}}.",
  'preorder.preparingToast': 'Your half-time pre-order is being prepared',
  'preorder.stripScheduled': 'Half-time pre-order confirmed',
  'preorder.stripPreparing': 'Half-time pre-order: being prepared',
  'preorder.stripReady': 'Half-time pre-order: ready at {{point}}!',
  'preorder.stripProcessing': 'Half-time pre-order: processing payment…',
  'preorder.orderCode': 'Order code',
  'preorder.switchedToNow': 'Half-time pre-orders just closed — your cart is now a regular order.',
  'preorder.closedNoNow': 'Half-time pre-orders just closed — pick-up is no longer available for this match.',
  'preorder.errorClosed': 'Half-time pre-orders have closed for this match — you can still order for now.',
  'preorder.errorDisabled': 'Half-time pre-orders are not available at this club.',
  'preorder.errorRequiresPickup': 'Half-time pre-orders need a collection point in your section.',
  'preorder.errorRequiresOnline': 'Half-time pre-orders must be paid online.',

  // Report a problem (fan page header chip + sheet)
  'report.chip': 'Report',
  'report.title': 'Report a problem',
  'report.subtitle': 'Tell us what went wrong. We read every report during the match.',
  'report.category.ordering': 'Ordering',
  'report.category.match_info': 'Match info',
  'report.category.page_not_loading': 'Page not loading',
  'report.category.other': 'Other',
  'report.placeholder': 'What happened?',
  'report.hint': 'Seat {seat} and this screen are attached automatically.',
  'report.send': 'Send report',
  'report.sending': 'Sending…',
  'report.sentTitle': "Thanks, we're on it",
  'report.sentBody': 'Your report reached the IDSeat team. You can go back to the match.',
  'report.close': 'Close',
  'report.errorGeneric': 'Could not send your report — please try again.',
  'report.rateLimited': 'Too many reports from this seat — please try again later.',

  // Live quiz (fan page button + sheet)
  'quiz.button': 'Live quiz',
  'quiz.buttonLive': 'Quiz · win {{prize}}',
  'quiz.buttonResults': 'Quiz results',
  'quiz.title': 'Live quiz',
  'quiz.prize': 'Win: {{prize}}',
  'quiz.intro': '{{count}} questions, {{seconds}} s each. The faster you answer, the more points you get. You play as a number — no name needed.',
  'quiz.start': 'Start quiz',
  'quiz.youAre': 'You are Player {{number}}',
  'quiz.startsIn': 'Starts in {{seconds}} s',
  'quiz.speedHint': 'Thumbs ready — speed counts.',
  'quiz.loading': 'Loading the question…',
  'quiz.questionOf': 'Question {{number}} of {{total}}',
  'quiz.getReady': 'Get ready…',
  'quiz.secondsLeft': '{{seconds}} s',
  'quiz.answerLocked': 'Answer locked in',
  'quiz.timeUp': "Time's up — the answer is coming…",
  'quiz.everyonePicked': 'What everyone picked so far',
  'quiz.yourPick': 'Your pick',
  'quiz.correct': 'Correct!',
  'quiz.wrong': 'Not this time',
  'quiz.noAnswer': 'No answer',
  'quiz.plusPoints': '+{{points}} pts',
  'quiz.totalPoints': '{{points}} pts total',
  'quiz.nextIn': 'Next question in {{seconds}} s',
  'quiz.resultsIn': 'Results in {{seconds}} s',
  'quiz.finishedTitle': 'Quiz over',
  'quiz.won': 'Congratulations, you won!',
  'quiz.unlucky': 'Unlucky, try again next time!',
  'quiz.rank': '#{{rank}} of {{players}}',
  'quiz.claimCode': 'Your prize code',
  'quiz.claimHint': 'Show this code to the club staff to collect your prize.',
  'quiz.winner': 'Winner: {{label}}',
  'quiz.topPlayers': 'Top players',
  'quiz.notPlayed': 'You did not take part in this quiz.',
  'quiz.rankPending': 'Working out your rank…',
  'quiz.cancelled': 'The quiz was cancelled',
  'quiz.answeredElsewhere': 'This seat already answered this question.',
  'quiz.tooLate': 'Too late — the question is closed.',
  'quiz.answerFailed': 'Could not send your answer — please try again.',

  // Stadium screens (public /screen/<token>/… pages). One audience per screen,
  // so the club picks the language on the Screens tab; the lines below are
  // the only fixed copy on the screens and the club edits them per feature.
  'screen.presentedBy': 'Presented by',
  'screen.reconnecting': 'Reconnecting',
  'screen.scanToJoin': 'Scan to vote & predict',
  'screen.where.seat': "on your seat's tag",
  'screen.where.seatOrQr': "on your seat's tag or the big-screen QR",
  'screen.join.camera': 'Point your camera at the code. No app, no seat needed.',
  'screen.join.unavailable': 'This screen tag is not available. Check the Screens page in the club portal.',
  'screen.join.refreshing': 'Reconnecting… the code on this screen is being refreshed',
  'screen.motm.title': 'Man of the Match',
  'screen.motm.opensAt': 'Voting opens at {{minute}}′ {{where}}',
  'screen.motm.opensDuring': 'Voting opens during the match',
  'screen.motm.closed': 'Voting has closed',
  'screen.motm.closedNoVotes': 'Voting closed without any votes',
  'screen.motm.winnerPending': 'The winner is announced when voting closes',
  'screen.motm.yourWinner': 'Your Man of the Match',
  'screen.motm.live': 'LIVE',
  'screen.motm.closedBadge': 'CLOSED',
  'screen.motm.votes': '{{count}} votes',
  'screen.motm.ofTheVote': '{{pct}}% of the vote',
  'screen.motm.beFirst': 'Be the first to vote {{where}}',
  'screen.predictor.title': 'Score predictor',
  'screen.predictor.drawAfter': 'The winner is drawn after the final whistle',
  'screen.predictor.nobody': 'Nobody guessed {{score}} this time',
  'screen.predictor.betterLuck': 'Better luck at the next match',
  'screen.predictor.winner': 'We have a winner',
  'screen.predictor.finalLine': 'Final score {{score}} · {{count}} fans got it right · one was drawn',
  'screen.predictor.stay': 'Stay in your seat. Your prize is on its way.',
  'screen.predictor.whatStadium': 'What the stadium predicts · {{count}} fans',
  'screen.predictor.ofFans': '{{pct}}% of fans',
  'screen.predictor.beFirst': 'Be the first to predict {{where}}',
  'screen.quiz.title': 'Live quiz',
  'screen.quiz.startsWhen': 'The quiz starts when the club presses Start',
  'screen.quiz.win': 'Win: {{prize}}',
  'screen.quiz.startsOnStart': 'Starts when the club presses Start',
  'screen.quiz.play': 'Play {{where}}',
  'screen.quiz.cancelled': 'The quiz was cancelled',
  'screen.quiz.players': '{{count}} players',
  'screen.quiz.startsIn': 'Starts in {{seconds}} s',
  'screen.quiz.introLine': 'Play {{where}} · {{count}} questions · faster answers score more',
  'screen.quiz.winner': 'We have a winner',
  'screen.quiz.nobodyScored': 'Nobody scored this time',
  'screen.quiz.results': 'Results…',
  'screen.quiz.winnerLabel': 'Winner',
  'screen.quiz.pts': '{{points}} pts',
  'screen.quiz.prizeOnPhone': "The prize code is on the winner's phone — see the club staff to collect it.",
  'screen.quiz.thanks': 'Thanks for playing',
  'screen.quiz.nextQuestion': 'Next question…',
  'screen.quiz.questionOf': 'Question {{n}} of {{total}}',
  'screen.quiz.getReady': 'Get ready…',
  'screen.quiz.timesUp': "Time's up",
};

// Get all translation keys organized by category
export const getTranslationKeysByCategory = () => {
  const categories: Record<string, string[]> = {
    'Common': [],
    'Predictor': [],
    'Survey': [],
    'Lineups': [],
    'Match Analytics': [],
    'Consent': [],
    'Errors': [],
    'Prizes': [],
    'Live Score': [],
    'Ordering': [],
    'Half-time pre-orders': [],
    'Report a problem': [],
    'Live quiz · phones': [],
    // The stadium screens' lines, per feature — the same lines the Screens tab's Texts button edits.
    'Screens · Man of the Match': [],
    'Screens · Score predictor': [],
    'Screens · Live quiz': [],
    'Screens · Join screen': [],
    'Screens · shared lines': [],
  };

  Object.keys(DEFAULT_TRANSLATIONS).forEach(key => {
    const prefix = key.split('.')[0];
    switch (prefix) {
      case 'common':
        categories['Common'].push(key);
        break;
      case 'predictor':
        categories['Predictor'].push(key);
        break;
      case 'survey':
        categories['Survey'].push(key);
        break;
      case 'lineups':
        categories['Lineups'].push(key);
        break;
      case 'match':
        categories['Match Analytics'].push(key);
        break;
      case 'consent':
        categories['Consent'].push(key);
        break;
      case 'error':
        categories['Errors'].push(key);
        break;
      case 'prizes':
        categories['Prizes'].push(key);
        break;
      case 'liveScore':
        categories['Live Score'].push(key);
        break;
      case 'ordering':
        categories['Ordering'].push(key);
        break;
      case 'preorder':
        categories['Half-time pre-orders'].push(key);
        break;
      case 'report':
        categories['Report a problem'].push(key);
        break;
      case 'quiz':
        categories['Live quiz · phones'].push(key);
        break;
      case 'screen': {
        const part = key.split('.')[1];
        const cat = part === 'motm' ? 'Screens · Man of the Match'
          : part === 'predictor' ? 'Screens · Score predictor'
          : part === 'quiz' ? 'Screens · Live quiz'
          : part === 'join' ? 'Screens · Join screen'
          : 'Screens · shared lines';
        categories[cat].push(key);
        break;
      }
    }
  });

  return categories;
};
